import React, { useState } from "react";
import Modal from "@/components/modal";

export default function ApplicationDetailsModal({ isOpen, onClose, application }) {
  const [activeTab, setActiveTab] = useState("info");

  if (!application) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "APPLIED":
        return "bg-blue-100 text-blue-800";
      case "PRESELECTED":
        return "bg-yellow-100 text-yellow-800";
      case "INTERVIEW":
        return "bg-purple-100 text-purple-800";
      case "SELECTED":
        return "bg-green-100 text-green-800";
      case "REFUSED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const tabs = [
    { id: "info", label: "Info" },
    { id: "raw", label: "Raw" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl w-full">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Application Details</h2>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-4" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id ? "border-purple-600 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Raw Tab */}
        {activeTab === "raw" && (
          <div className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-[60vh]">
            <pre className="text-xs text-gray-900 whitespace-pre-wrap font-mono">{JSON.stringify(application, null, 2)}</pre>
          </div>
        )}

        {/* Info Tab */}
        {activeTab === "info" && (
          <>
            {/* Main Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Candidate Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Candidate Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
              <div className="text-sm text-gray-900">{application.candidate_name || "-"}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Type</label>
              <div className="text-sm text-gray-900">{application.candidate_type || "-"}</div>
            </div>
          </div>

          {/* Job Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Job Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Job Title</label>
              <div className="text-sm text-gray-900">{application.job_title || "-"}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Job Code</label>
              <div className="text-sm text-gray-900">{application.job_code || "-"}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Organization</label>
              <div className="text-sm text-gray-900">{application.organization_name || "-"}</div>
            </div>
          </div>
        </div>

        {/* Application Status */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Application Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
              <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${getStatusColor(application.status)}`}>{application.status || "-"}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Application Date</label>
              <div className="text-sm text-gray-900">{application.application_date ? new Date(application.application_date).toLocaleString("fr-FR") : "-"}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Source</label>
              <div className="text-sm text-gray-900">{application.source || "-"}</div>
            </div>
          </div>
        </div>

        {/* Matching Scores */}
        <div className="bg-purple-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Matching Scores</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white p-3 rounded-lg">
              <label className="block text-xs font-medium text-gray-600 mb-1">Global Score</label>
              <div className="text-2xl font-bold text-purple-600">{application.global_matching_score || 0}%</div>
            </div>
            {application.skills_matching_score_calculated !== undefined && (
              <div className="bg-white p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Skills</label>
                <div className="text-2xl font-bold text-blue-600">{application.skills_matching_score_calculated}%</div>
              </div>
            )}
            {application.intelli7_matching_score_calculated !== undefined && (
              <div className="bg-white p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Intelli7</label>
                <div className="text-2xl font-bold text-green-600">{application.intelli7_matching_score_calculated}%</div>
              </div>
            )}
            {application.experiences_matching_score_calculated !== undefined && (
              <div className="bg-white p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Experience</label>
                <div className="text-2xl font-bold text-yellow-600">{application.experiences_matching_score_calculated}%</div>
              </div>
            )}
            {application.accreditations_matching_score_calculated !== undefined && (
              <div className="bg-white p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Accreditations</label>
                <div className="text-2xl font-bold text-indigo-600">{application.accreditations_matching_score_calculated}%</div>
              </div>
            )}
            {application.availabilities_matching_score_calculated !== undefined && (
              <div className="bg-white p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Availability</label>
                <div className="text-2xl font-bold text-pink-600">{application.availabilities_matching_score_calculated}%</div>
              </div>
            )}
            {application.licences_matching_score_calculated !== undefined && (
              <div className="bg-white p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Licenses</label>
                <div className="text-2xl font-bold text-teal-600">{application.licences_matching_score_calculated}%</div>
              </div>
            )}
            {application.distance_in_km !== undefined && (
              <div className="bg-white p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Distance</label>
                <div className="text-2xl font-bold text-orange-600">{application.distance_in_km} km</div>
              </div>
            )}
          </div>
        </div>

        {/* Skills */}
        {application.skill && application.skill.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {application.skill.map((skill, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* AI Q&A */}
        {application.job_application_ai_qna && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">AI Q&A</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <pre className="text-sm text-gray-900 whitespace-pre-wrap">{JSON.stringify(application.job_application_ai_qna, null, 2)}</pre>
            </div>
          </div>
        )}

        {/* Screening Score */}
        {application.screening_score && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Screening Score</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <pre className="text-sm text-gray-900 whitespace-pre-wrap">{JSON.stringify(application.screening_score, null, 2)}</pre>
            </div>
          </div>
        )}

        {/* Push Information */}
        {application.pushed && (
          <div className="bg-green-50 p-4 rounded-lg mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Push Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Pushed By</label>
                <div className="text-sm text-gray-900">{application.pushed_by || "-"}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Push Date</label>
                <div className="text-sm text-gray-900">{application.push_date ? new Date(application.push_date).toLocaleString("fr-FR") : "-"}</div>
              </div>
            </div>
          </div>
        )}

            {/* Timestamps */}
            <div className="border-t pt-4 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-600">
                <div>
                  <span className="font-medium">Created:</span> {application.created_at ? new Date(application.created_at).toLocaleString("fr-FR") : "-"}
                </div>
                <div>
                  <span className="font-medium">Updated:</span> {application.updated_at ? new Date(application.updated_at).toLocaleString("fr-FR") : "-"}
                </div>
                {application.matching_date && (
                  <div>
                    <span className="font-medium">Matching Date:</span> {new Date(application.matching_date).toLocaleString("fr-FR")}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
